import { useCallback, useMemo } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { actionsFromServerPerms, checkAction, isHighRisk, type Action } from '@/lib/permissions';

/**
 * Permission hook — the only way components ask "can I?". Delegates to
 * lib/permissions (single source of truth).
 *
 * FIX(perm-admin-keys-only): capability is derived from the session's permission
 * KEYS alone — `/me`'s `permissions` (platform keys) and `platformSurfacePermissions`
 * (what `requirePlatformPerm` grants). No role name is consulted anywhere: not a
 * `superadmin` wildcard, not a `requireAdmin` slug, not a role-matrix fallback, not
 * a workspace_admin scope rule. A payload that carries no list grants nothing. The
 * server remains the authority on every actual request.
 */
export function usePermission() {
  const { user } = useAuth();
  const permissions = user?.permissions;
  const permissionsKnown = user?.permissionsKnown ?? false;
  const platformSurfacePermissions = user?.platformSurfacePermissions;

  const granted = useMemo<ReadonlySet<Action>>(() => {
    if (!user || !permissionsKnown) return new Set<Action>();
    return actionsFromServerPerms(permissions ?? [], platformSurfacePermissions);
  }, [user, permissions, permissionsKnown, platformSurfacePermissions]);

  const check = useCallback(
    // `_ctx` is kept so `<Can workspaceId>` call sites still type-check; there is
    // no client-side workspace scoping any more (the server enforces it).
    (action: Action, _ctx: { workspaceId?: string } = {}) => checkAction(granted, action),
    [granted],
  );

  return { can: check, isHighRisk };
}
